import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/admin/AdminLayout";
import DataTable from "../components/admin/DataTable";
import api from "../api";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "expired", label: "Expired" },
  { id: "all", label: "All" },
];

const CATEGORY_LABEL = { student: "Student", senior: "Senior Citizen" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const fmtDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { dateStyle: "medium" });
};

const ageFromDob = (dob) => {
  const d = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) {
    age -= 1;
  }
  return age;
};

const detailsText = (row) => {
  if (row.category === "student") {
    return `${row.details?.institution || "—"} · ${row.details?.academicYear || "—"}`;
  }
  const dob = row.details?.dob;
  if (!dob) return "—";
  const age = ageFromDob(dob);
  return `DOB ${fmtDay(dob)}${age === null ? "" : ` (age ${age})`}`;
};

const StatusChip = ({ status }) => (
  <span className={`verif-chip verif-chip--${status}`}>{status}</span>
);

// ── Review modal ──────────────────────────────────────────────────────────────
const ReviewModal = ({ row, onClose, onDone }) => {
  const [docUrl, setDocUrl] = useState("");
  const [docMime, setDocMime] = useState("");
  const [docError, setDocError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  // The document route needs the admin Bearer token, so it can't be an <img src>.
  // Fetch it as a blob and show it from an object URL.
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    (async () => {
      try {
        const res = await api.get(`/payment/admin/verifications/${encodeURIComponent(row.id)}/document`, {
          responseType: "blob",
        });
        if (!active) return;
        objectUrl = URL.createObjectURL(res.data);
        setDocMime(res.data.type || row.document?.mime || "");
        setDocUrl(objectUrl);
      } catch {
        if (active) setDocError("Could not load the uploaded document.");
      }
    })();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [row.id, row.document?.mime]);

  const act = async (kind) => {
    setBusy(true);
    setError("");
    try {
      await api.post(
        `/payment/admin/verifications/${encodeURIComponent(row.id)}/${kind}`,
        kind === "reject" ? { reason } : {}
      );
      onDone(kind === "approve" ? "Request approved." : "Request rejected.");
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Try again.");
      setBusy(false);
    }
  };

  const isPdf = docMime === "application/pdf";
  const canReview = row.status === "pending";

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="modal-card" style={{ width: "min(760px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <h2>
          {CATEGORY_LABEL[row.category] || row.category} plan request{" "}
          <StatusChip status={row.status} />
        </h2>

        <div className="verif-grid">
          <div><span>Name</span><strong>{row.name || "—"}</strong></div>
          <div><span>Email</span><strong>{row.email || "—"}</strong></div>
          <div><span>Phone</span><strong>{row.phone || "—"}</strong></div>
          <div><span>Submitted</span><strong>{fmtDate(row.submittedAt)}</strong></div>
          <div style={{ gridColumn: "1 / -1" }}>
            <span>{row.category === "student" ? "Institution / academic year" : "Date of birth entered by user"}</span>
            <strong>{detailsText(row)}</strong>
          </div>
          {row.reviewedAt && (
            <div style={{ gridColumn: "1 / -1" }}>
              <span>Reviewed</span>
              <strong>
                {fmtDate(row.reviewedAt)} by {row.reviewedBy || "—"}
                {row.rejectReason ? ` — ${row.rejectReason}` : ""}
                {row.validUntil ? ` · valid until ${fmtDate(row.validUntil)}` : ""}
              </strong>
            </div>
          )}
        </div>

        <div className="verif-checklist">
          {row.category === "student" ? (
            <>
              <strong>Check before approving</strong>
              <ul>
                <li>Valid school or educational institution ID card.</li>
                <li>Belongs to the current academic year.</li>
                <li>Name on the card matches the account.</li>
              </ul>
            </>
          ) : (
            <>
              <strong>Check before approving</strong>
              <ul>
                <li>Aadhaar or another valid government-issued ID.</li>
                <li>Date of birth or age is clearly visible and shows 60 or above.</li>
                <li>Matches the date of birth entered above.</li>
              </ul>
            </>
          )}
        </div>

        <div className="verif-doc">
          {docError && <p className="error-banner">{docError}</p>}
          {!docError && !docUrl && <p className="loading-text">Loading document…</p>}
          {docUrl && isPdf && (
            <iframe title="Uploaded document" src={docUrl} style={{ width: "100%", height: 420, border: 0 }} />
          )}
          {docUrl && !isPdf && (
            <img src={docUrl} alt="Uploaded document" style={{ maxWidth: "100%", maxHeight: 460, display: "block", margin: "0 auto" }} />
          )}
          {docUrl && (
            <p style={{ margin: "8px 0 0", textAlign: "center" }}>
              <a href={docUrl} target="_blank" rel="noreferrer">Open in a new tab</a>
            </p>
          )}
        </div>

        {error && <p className="error-banner">{error}</p>}

        {canReview && rejecting && (
          <div style={{ marginTop: 12 }}>
            <label htmlFor="reject-reason">Reason shown to the user</label>
            <textarea
              id="reject-reason"
              rows={3}
              maxLength={300}
              placeholder="e.g. The ID card is not for the current academic year"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        )}

        <div className="modal-actions" style={{ marginTop: 12 }}>
          {canReview && !rejecting && (
            <>
              <button type="button" className="primary-action-btn" disabled={busy} onClick={() => act("approve")}>
                {busy ? "Working…" : "Approve"}
              </button>
              <button type="button" className="ghost-btn verif-reject" disabled={busy} onClick={() => setRejecting(true)}>
                Reject
              </button>
            </>
          )}
          {canReview && rejecting && (
            <>
              <button
                type="button"
                className="primary-action-btn verif-reject-confirm"
                disabled={busy || reason.trim().length < 3}
                onClick={() => act("reject")}
              >
                {busy ? "Working…" : "Confirm rejection"}
              </button>
              <button type="button" className="ghost-btn" disabled={busy} onClick={() => setRejecting(false)}>
                Back
              </button>
            </>
          )}
          <button type="button" className="ghost-btn" disabled={busy} onClick={onClose} style={{ marginLeft: "auto" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const SpecialPlanRequestsPage = ({ module }) => {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (status) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/payment/admin/verifications", { params: { status } });
      setRows(Array.isArray(data.verifications) ? data.verifications : []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const columns = useMemo(
    () => [
      { key: "submittedAt", label: "Submitted", accessor: "submittedAt", render: (r) => fmtDate(r.submittedAt) },
      {
        key: "category",
        label: "Plan",
        accessor: (r) => CATEGORY_LABEL[r.category] || r.category,
        render: (r) => <strong>{CATEGORY_LABEL[r.category] || r.category}</strong>,
      },
      { key: "name", label: "Name", accessor: "name", render: (r) => r.name || "—" },
      {
        key: "contact",
        label: "Contact",
        accessor: (r) => `${r.email} ${r.phone}`,
        render: (r) => (
          <div>
            <div>{r.email || "—"}</div>
            <div style={{ color: "#6b7280", fontSize: 12 }}>{r.phone || ""}</div>
          </div>
        ),
      },
      { key: "details", label: "Details", accessor: (r) => detailsText(r), render: (r) => detailsText(r) },
      { key: "status", label: "Status", accessor: "status", render: (r) => <StatusChip status={r.status} /> },
      {
        key: "action",
        label: "",
        sortable: false,
        searchable: false,
        accessor: () => "",
        render: (r) => (
          <button type="button" className="ghost-btn" onClick={() => setSelected(r)}>
            {r.status === "pending" ? "Review" : "View"}
          </button>
        ),
      },
    ],
    []
  );

  return (
    <AdminLayout module={module} title="Special Plan Requests">
      <div className="warning-banner" style={{ marginBottom: 16 }}>
        Student and Senior Citizen plans are only available after you approve the uploaded ID.
        Student approvals are valid for 12 months. Documents are private and visible only here.
      </div>

      {notice && <p className="success-banner">{notice}</p>}
      {error && <p className="error-banner">{error}</p>}

      <div className="verif-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? "is-active" : ""}
            onClick={() => {
              setNotice("");
              setTab(t.id);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <p className="loading-text">Loading requests…</p> : <DataTable columns={columns} rows={rows} />}

      {selected && (
        <ReviewModal
          row={selected}
          onClose={() => setSelected(null)}
          onDone={(message) => {
            setSelected(null);
            setNotice(message);
            load(tab);
          }}
        />
      )}
    </AdminLayout>
  );
};

export default SpecialPlanRequestsPage;
